import { useEffect, useRef } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useAppState } from "../../../contexts/UserContext";
import { useTeam } from "../../../contexts/TeamContext";
import joinOrganizationByEmailDomain from "../../../utils/teams/joinOrganizationByEmailDomain";

interface UseAutoJoinOrganizationByEmailDomainOptions {
  organizationId: string | null;
  teamId: string | null;
}

/**
 * When the user has no organization, checks subscribed organization leads for
 * a matching email domain and auto-joins the user to that organization.
 */
export function useAutoJoinOrganizationByEmailDomain({
  organizationId,
  teamId,
}: UseAutoJoinOrganizationByEmailDomainOptions) {
  const { userId, email } = useAuth();
  const { refreshData } = useAppState();
  const { setOrganizationId, setTeamId } = useTeam();
  const hasAttemptedRef = useRef<string | null>(null);

  useEffect(() => {
    if (organizationId != null || teamId != null) {
      return;
    }
    if (!userId || !email) {
      return;
    }
    if (hasAttemptedRef.current === userId) {
      return;
    }

    hasAttemptedRef.current = userId;

    (async () => {
      const result = await joinOrganizationByEmailDomain({
        uid: userId,
        email,
      });

      if (!result.success || !result.organizationId) {
        if (
          result.reason !== "already_has_organization" &&
          result.reason !== "already_has_membership"
        ) {
          hasAttemptedRef.current = null;
        }
        console.log(
          "useAutoJoinOrganizationByEmailDomain: no auto-join",
          result.reason ?? result.error ?? "unknown"
        );
        return;
      }

      setOrganizationId(result.organizationId);
      setTeamId(result.organizationId);

      try {
        await refreshData(userId, "all");
      } catch (refreshError) {
        console.warn(
          "useAutoJoinOrganizationByEmailDomain: failed to refresh user data",
          refreshError
        );
      }
    })();
  }, [
    organizationId,
    teamId,
    userId,
    email,
    refreshData,
    setOrganizationId,
    setTeamId,
  ]);
}
